import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint no-param-reassign: 0 */

import { NewNumberModel, OperatorModel } from '../model/line.model'

import { SignUp, SignUpType, Owner, PortabilityType } from '~/basket/model/lines/signup.model'

interface Error {
  lineId: string
  errorMessage: string
  errorCode: string
}

interface SuccessNewNumber extends NewNumberModel {
  lineId: string
}

interface SuccessOperator extends OperatorModel {
  lineId: string
}

interface SetFields extends SignUp {
  lineId: string
}

export type SingleLine = {
  newNumber: {
    loading: boolean | null
    errorCode: string
    errorMessage: string
    data: NewNumberModel | Record<string, any>
  }
  operator: {
    loading: boolean | null
    errorCode: string
    errorMessage: string
    data: OperatorModel | Record<string, any>
  }
  values: { signUp: SignUp }
}

export type LineState = {
  [key: string]: SingleLine
}

export const lineInitialValues = {
  signUp: {
    signUpType: SignUpType.EMPTY,
    lineNumber: '',
    icc: '',
    donorICC: '',
    portabilityType: PortabilityType.EMPTY,
    isSameOwner: Owner.EMPTY,
    donorOwner: {
      name: '',
      firstName: '',
      lastName: '',
      docType: '',
      docId: '',
      companyName: '',
    },
  },
}

const lineStructure = {
  newNumber: {
    data: {},
    errorMessage: '',
    errorCode: '',
    loading: null,
  },
  operator: {
    data: {},
    errorMessage: '',
    errorCode: '',
    loading: null,
  },
  values: lineInitialValues,
}

interface IccBalancePayload {
  lineId: string
  balance?: number
  available: boolean
}
export const initialState: LineState = {
  mainLine: lineStructure,
}

export const lineSlice = createSlice({
  name: 'line',
  initialState,
  reducers: {
    createNewLineStructure: (state, action: PayloadAction<string>) => {
      const { payload } = action
      return { ...state, [payload]: lineStructure }
    },
    deleteLine: (state, action: PayloadAction<string>) => {
      const { payload } = action
      delete state[payload]
      return state
    },
    fetchNewNumberData: (state, action: PayloadAction<string>) => {
      const { payload } = action
      return {
        ...state,
        [payload]: {
          ...state[payload],
          newNumber: { ...lineStructure.newNumber, loading: true },
        },
      }
    },
    fetchNewNumberDataSuccess(state, action: PayloadAction<SuccessNewNumber>) {
      const {
        payload: { phoneNumber, lineId },
      } = action
      return {
        ...state,
        [lineId]: {
          ...state[lineId],
          newNumber: { ...lineStructure.newNumber, data: { phoneNumber }, loading: false },
        },
      }
    },
    fetchNewNumberDataError(state, action: PayloadAction<Error>) {
      const {
        payload: { errorMessage, errorCode, lineId },
      } = action

      return {
        ...state,
        [lineId]: {
          ...state[lineId],
          newNumber: { ...lineStructure.newNumber, errorCode, errorMessage, loading: false },
        },
      }
    },
    fetchOperatorData: (state, action: PayloadAction<{ id: string; lineNumber: string }>) => {
      const { payload } = action
      return {
        ...state,
        [payload.id]: {
          ...state[payload.id],
          operator: { ...lineStructure.operator, loading: true },
        },
      }
    },
    fetchOperatorSuccess(state, action: PayloadAction<SuccessOperator>) {
      const {
        payload: { lineId, ...data },
      } = action
      return {
        ...state,
        [lineId]: {
          ...state[lineId],
          operator: { ...lineStructure.operator, loading: false, data },
        },
      }
    },
    fetchOperatorError(state, action: PayloadAction<Error>) {
      const {
        payload: { errorMessage, errorCode, lineId },
      } = action

      return {
        ...state,
        [lineId]: {
          ...state[lineId],
          operator: {
            ...lineStructure.operator,
            errorCode,
            errorMessage,
            loading: false,
          },
        },
      }
    },
    resetOperatorData(state, action: PayloadAction<string>) {
      const { payload } = action
      return {
        ...state,
        [payload]: {
          ...state[payload],
          operator: { ...lineStructure.operator },
        },
      }
    },
    setIccBalance(state, action: PayloadAction<IccBalancePayload>) {
      const { lineId } = action.payload
      return {
        ...state,
        [lineId]: {
          ...state[lineId],
          values: {
            ...state[lineId].values,
            signUp: {
              ...state[lineId].values.signUp,
              iccBalance: action.payload.balance,
              iccAvailable: action.payload.available,
            },
          },
        },
      }
    },
    setFields(state, action: PayloadAction<SetFields>) {
      const {
        payload: { lineId, ...values },
      } = action

      return {
        ...state,
        [lineId]: {
          ...state[lineId],
          values: { signUp: { ...values, iccBalance: state[lineId].values.signUp.iccBalance } },
        },
      }
    },
    resetLines() {
      return initialState
    },
  },
})

export const {
  createNewLineStructure,
  deleteLine,
  fetchNewNumberData,
  fetchNewNumberDataSuccess,
  fetchNewNumberDataError,
  fetchOperatorData,
  fetchOperatorSuccess,
  fetchOperatorError,
  resetOperatorData,
  setFields,
  setIccBalance,
  resetLines,
} = lineSlice.actions

const lineReducer = lineSlice.reducer
export default lineReducer
